import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function DocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const token = localStorage.getItem('token');
  const axiosConfig = { headers: { 'Authorization': `Bearer ${token}` } };

  useEffect(() => {
    fetchDocuments();
    fetchUser();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/my`, axiosConfig);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, axiosConfig);
      setUser(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64Data = reader.result;
        
        const response = await axios.post(`${API}/documents/upload`, {
          document_type: docType,
          file_data: base64Data,
          file_name: file.name
        }, axiosConfig);
        
        setModalMessage('Belge başarıyla yüklendi!');
        setShowSuccessModal(true);
        fetchDocuments();
        fetchUser();
        setUploading(false);
      };
      
      reader.onerror = () => {
        setModalMessage('Dosya okunamadı!');
        setShowErrorModal(true);
        setUploading(false);
      };
    } catch (err) {
      setModalMessage(err.response?.data?.detail || err.message);
      setShowErrorModal(true);
      setUploading(false);
    }
  };

  // Get overall document status - prioritize the best status
  const getOverallStatus = () => {
    if (!user) return 'PENDING';
    
    // Check if any document is APPROVED
    const hasApproved = documents.some(doc => doc.status === 'APPROVED');
    if (hasApproved) return 'APPROVED';
    
    // Check if any document is REVIEWING
    const hasReviewing = documents.some(doc => doc.status === 'REVIEWING');
    if (hasReviewing) return 'REVIEWING';
    
    // Check if any document is REJECTED
    const hasRejected = documents.some(doc => doc.status === 'REJECTED');
    if (hasRejected) return 'REJECTED';
    
    // Default to PENDING
    return 'PENDING';
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Belge Yükle</h1>
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
              ← Geri
            </button>
          </div>

          {/* Overall Status Banner */}
          <div className={`mb-6 p-4 rounded-lg border-2 ${
            getOverallStatus() === 'APPROVED' ? 'bg-green-900/30 border-green-600' :
            getOverallStatus() === 'REJECTED' ? 'bg-red-900/30 border-red-600' :
            getOverallStatus() === 'REVIEWING' ? 'bg-yellow-900/30 border-yellow-600' :
            'bg-gray-800 border-gray-600'
          }`}>
            <div className="flex items-center gap-3">
              <div className="text-3xl">
                {getOverallStatus() === 'APPROVED' ? '✅' :
                 getOverallStatus() === 'REJECTED' ? '❌' :
                 getOverallStatus() === 'REVIEWING' ? '🔍' :
                 '⏳'}
              </div>
              <div>
                <div className="font-bold text-lg">
                  {getOverallStatus() === 'APPROVED' ? 'Belgeniz Onaylandı!' :
                   getOverallStatus() === 'REJECTED' ? 'Belgeniz Reddedildi' :
                   getOverallStatus() === 'REVIEWING' ? 'İnceleniyor' :
                   'Onay Bekleniyor'}
                </div>
                <div className="text-sm text-gray-300 mt-1">
                  {getOverallStatus() === 'APPROVED' ? 'Para yatırma işlemlerine başlayabilirsiniz.' :
                   getOverallStatus() === 'REJECTED' ? 'Lütfen belgelerinizi tekrar yükleyin.' :
                   getOverallStatus() === 'REVIEWING' ? 'Admin belgenizi inceliyor. Lütfen bekleyin.' :
                   'Belgeniz admin onayı bekliyor.'}
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-6">
            <h3 className="font-bold mb-2">📝 Belge Yükleme</h3>
            <ul className="text-sm space-y-1">
              <li>• Kimlik belgesi (TC Kimlik, Ehliyet, Pasaport)</li>
              <li>• Belgeler açık ve okunarlı olmalıdır</li>
              <li>• JPG, PNG veya PDF formatında</li>
            </ul>
          </div>

          {/* Upload Button */}
          <div className="mb-8">
            <label className="block text-gray-300 mb-3 font-semibold">Kimlik Belgesi Yükle</label>
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileUpload(e, 'ID_CARD')}
                className="hidden"
                id="identity-upload"
                disabled={uploading}
              />
              <label htmlFor="identity-upload" className="cursor-pointer">
                <div className="text-4xl mb-2">📄</div>
                <div className="text-gray-400">
                  {uploading ? 'Yükleniyor...' : 'Tıklayarak kimlik belgesi yükleyin'}
                </div>
                <div className="text-xs text-gray-500 mt-1">JPG, PNG veya PDF</div>
              </label>
            </div>
          </div>

          {/* Uploaded Documents List */}
          <div>
            <h3 className="text-xl font-bold mb-4">📋 Yüklenen Belgeler</h3>
            
            {documents.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-6 text-center">
                <div className="text-gray-400 mb-2">📭</div>
                <p className="text-gray-400">Henüz belge yüklemediniz</p>
                <p className="text-sm text-gray-500 mt-1">Yukarıdan belge yükleyebilirsiniz</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Approved Documents */}
                {documents.filter(d => d.status === 'APPROVED').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-400 mb-2">✅ Onaylanan Belgeler</h4>
                    <div className="space-y-2">
                      {documents.filter(d => d.status === 'APPROVED').map(doc => (
                        <div key={doc.id} className="bg-green-900/20 border border-green-700 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">✅</span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{doc.document_type}</div>
                              <div className="text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleString('tr-TR')}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reviewing Documents */}
                {documents.filter(d => d.status === 'REVIEWING').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-400 mb-2">🔍 İncelenen Belgeler</h4>
                    <div className="space-y-2">
                      {documents.filter(d => d.status === 'REVIEWING').map(doc => (
                        <div key={doc.id} className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">🔍</span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{doc.document_type}</div>
                              <div className="text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleString('tr-TR')}</div>
                              <div className="text-xs text-yellow-300 mt-1">Admin incelemesi devam ediyor...</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Documents */}
                {documents.filter(d => d.status === 'PENDING').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">⏳ Onay Bekleyen Belgeler</h4>
                    <div className="space-y-2">
                      {documents.filter(d => d.status === 'PENDING').map(doc => (
                        <div key={doc.id} className="bg-gray-800 border border-gray-600 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">⏳</span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{doc.document_type}</div>
                              <div className="text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleString('tr-TR')}</div>
                              <div className="text-xs text-gray-400 mt-1">Admin onayı bekleniyor...</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejected Documents */}
                {documents.filter(d => d.status === 'REJECTED').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-400 mb-2">❌ Reddedilen Belgeler</h4>
                    <div className="space-y-2">
                      {documents.filter(d => d.status === 'REJECTED').map(doc => (
                        <div key={doc.id} className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">❌</span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{doc.document_type}</div>
                              <div className="text-xs text-gray-400">{new Date(doc.uploaded_at).toLocaleString('tr-TR')}</div>
                              <div className="text-xs text-red-300 mt-1">Lütfen tekrar yükleyin.</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-green-500/30 animate-fade-in">
            <div className="p-8 text-center">
              <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-green-500 animate-pulse">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Başarılı!</h3>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">{modalMessage}</p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-red-500/30 animate-fade-in">
            <div className="p-8 text-center">
              <div className="mx-auto w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-red-500">
                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Hata!</h3>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">{modalMessage}</p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentsPage;
